/**
 * 一覧の右パネル(幅 420、`width` で上書き)。開閉は `?panel=<id>` で親が持つ(戻るで閉じる)。
 *
 * 上の帯: 「全画面で開く」(詳細ページへ)+ 帯の操作(`barActions`: 承認 / 却下など、
 * 本文をスクロールしても見える)+ 閉じる。
 * 見出し: アバター + タイトル + 状態、サブ行。操作(`actions`)、本文は画面ごと。
 * `footer` は下に固定(「完了にする」など、本文の長さに関わらず押せる位置)。
 */
import { CloseOutlined, ExpandAltOutlined } from "@ant-design/icons";
import { Button, Spin } from "antd";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export function SidePanel({
  title,
  ariaLabel,
  avatar,
  status,
  sub,
  fullHref,
  onClose,
  actions,
  barActions,
  footer,
  width,
  loading = false,
  children,
}: {
  title: ReactNode;
  /** `aria-label`(省略時は「<title> の詳細」。title が文字でないときは渡す) */
  ariaLabel?: string;
  avatar?: ReactNode;
  status?: ReactNode;
  sub?: ReactNode;
  /** 詳細ページ。あれば「全画面で開く」を出す */
  fullHref?: string;
  onClose: () => void;
  /** 見出しの下の操作 */
  actions?: ReactNode;
  /** 上の帯の操作(スクロールしても見える。承認 / 却下など 1〜2 個) */
  barActions?: ReactNode;
  /** 下に固定する操作 */
  footer?: ReactNode;
  /** 幅の上書き(px)。文面を編集するパネルなど */
  width?: number;
  loading?: boolean;
  children?: ReactNode;
}) {
  const label = ariaLabel ?? (typeof title === "string" ? `${title} の詳細` : "詳細");
  const style = width ? { flexBasis: width, width } : undefined;
  return (
    <aside className="ui-panel" aria-label={label} data-testid="side-panel" style={style}>
      <div className="ui-panel-bar">
        {fullHref && (
          <Link to={fullHref}>
            <ExpandAltOutlined /> 全画面で開く
          </Link>
        )}
        <span className="ui-filter-spacer" />
        {barActions && <div className="ui-panel-bar-actions">{barActions}</div>}
        <Button type="text" size="small" icon={<CloseOutlined />} aria-label="閉じる" onClick={onClose} />
      </div>
      {loading ? (
        <div style={{ padding: 48, textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <>
          <div className="ui-panel-head">
            {avatar}
            <div className="ui-panel-head-text">
              <h2 className="ui-panel-title">
                <span>{title}</span>
                {status}
              </h2>
              {sub && <div className="ui-panel-sub">{sub}</div>}
            </div>
          </div>
          {actions && <div className="ui-panel-actions">{actions}</div>}
          <div className="ui-panel-body">{children}</div>
          {footer && <div className="ui-panel-footer">{footer}</div>}
        </>
      )}
    </aside>
  );
}

/** 右パネルの中の小見出し付きブロック。 */
export function PanelSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="ui-panel-section">
      <h3>{title}</h3>
      {children}
    </div>
  );
}
